import { fetchAuthSession } from "aws-amplify/auth";
import { generateClient } from "aws-amplify/api";
import amplifyOutputs from "@/amplify_outputs.json";

const client = generateClient();

const QUICKSIGHT_USER_MANAGEMENT_CONFIG = {
  lambdaUrl: amplifyOutputs.custom?.quickSightUserManagementFunctionUrl || "",
};

// Helper function to make authenticated requests to QuickSight user management Lambda
const makeQuickSightRequest = async (action, data = {}) => {
  try {
    const session = await fetchAuthSession();
    const jwtToken = session.tokens?.accessToken?.toString();
    if (!jwtToken) throw new Error("No authentication token found");
    const response = await fetch(QUICKSIGHT_USER_MANAGEMENT_CONFIG.lambdaUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        action,
        ...data,
      }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      console.error(`Lambda error response:`, errorData);
      // Create a more detailed error with all the information from Lambda
      const detailedError = new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      detailedError.reason = errorData.reason;
      detailedError.suggestion = errorData.suggestion;
      detailedError.userEmail = errorData.userEmail;
      detailedError.missing = errorData.missing;
      throw detailedError;
    }
    return await response.json();
  } catch (error) {
    console.error(`QuickSight error in ${action}:`, error);
    throw error;
  }
};

// Register a user in QuickSight
export const registerQuickSightUser = async (userEmail, userRole = "READER") => {
  return makeQuickSightRequest("REGISTER_USER", { userEmail, userRole });
};

// Delete a user from QuickSight
export const deleteQuickSightUser = async (userEmail) => {
  return makeQuickSightRequest("DELETE_USER", { userEmail });
};

// Invite a user to a dashboard
export const inviteUserToDashboard = async ({
  userEmail,
  dashboardId,
  dashboardName,
  dashboardLink,
  makeDefault = false,
  viewId,
  companyName,
}) => {
  return makeQuickSightRequest("INVITE_TO_DASHBOARD", {
    userEmail,
    dashboardId,
    dashboardName,
    dashboardLink,
    makeDefault,
    viewId,
    companyName,
  });
};

// Remove a user from a dashboard
export const removeUserFromDashboard = async ({
  userEmail,
  dashboardId,
  viewId,
}) => {
  return makeQuickSightRequest("REMOVE_FROM_DASHBOARD", {
    userEmail,
    dashboardId,
    viewId,
  });
};

// Set a dashboard as default for a user
export const setDashboardAsDefault = async ({
  userEmail,
  dashboardId,
  viewId,
}) => {
  return makeQuickSightRequest("SET_DASHBOARD_AS_DEFAULT", {
    userEmail,
    dashboardId,
    viewId,
  });
};

// List all QuickSight users
export const listQuickSightUsers = async () => {
  const response = await makeQuickSightRequest("LIST_QUICKSIGHT_USERS");
  return response.users || [];
};

// List dashboards for a user
export const getUserDashboards = async (userEmail) => {
  const response = await makeQuickSightRequest("GET_USER_DASHBOARDS", { userEmail });
  return response.dashboards || [];
};

// List all Cognito users with their QuickSight status
export const getCognitoUsersWithQuickSightStatus = async () => {
  const response = await makeQuickSightRequest("LIST_COGNITO_USERS_WITH_QUICKSIGHT_STATUS");
  return response.users || [];
};


// Helper function to get JWT token
export const getJwtToken = async () => {
  try {
    const session = await fetchAuthSession();
    return session.tokens.idToken.toString();
  } catch (error) {
    console.error("Error getting JWT token:", error);
    throw error;
  }
};

// Helper function to get user information
export const getUserInfo = async () => {
  try {
    const session = await fetchAuthSession();
    return {
      email: session.tokens.idToken.payload.email,
      sub: session.tokens.idToken.payload.sub,
      username: session.tokens.idToken.payload["cognito:username"],
      jwtToken: session.tokens.idToken.toString(),
      accessToken: session.tokens.accessToken.toString(),
      identityId: session.identityId,
    };
  } catch (error) {
    console.error("Error getting user info:", error);
    throw error;
  }
};


export const createDashboardMetadata = async ({
  dashboardId,
  dashboardName,
  dashboardLink,
  description = "",
}) => {
  return makeQuickSightRequest("CREATE_DASHBOARD_METADATA", {
    dashboardId,
    dashboardName,
    dashboardLink,
    description,
  });
};

export const listDashboardMetadata = async () => {
  const response = await makeQuickSightRequest("LIST_DASHBOARD_METADATA");
  return response.dashboards || [];
};


export const updateDashboardMetadata = async ({
  metadataId,
  dashboardId,
  dashboardName,
  dashboardLink,
  description,
  isActive,
}) => {
  return makeQuickSightRequest("UPDATE_DASHBOARD_METADATA", {
    metadataId,
    dashboardId,
    dashboardName,
    dashboardLink,
    description,
    isActive,
  });
};


export const deleteDashboardMetadata = async (metadataId) => {
  return makeQuickSightRequest("DELETE_DASHBOARD_METADATA", { metadataId });
};
